import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/Button/Button";
import { FaTimes, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";

/**
 * Modale par CLUB :
 * - Liste toutes les compétitions de l’organisateur
 * - Case cochée si le club a déjà une invitation pour cette compétition
 * - Coche -> assignOne(competition_id)
 * - Décoche -> removeOne(inviteId)
 */
const SlideOver = ({ open, onClose, title, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative ml-auto h-full w-full max-w-2xl bg-white dark:bg-slate-900 shadow-xl border-l border-gray-200 dark:border-slate-700 p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <button
            className="h-8 w-8 rounded-full bg-black/10 dark:bg-white/10"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
};

const ManageClubCompetitionsModal = ({
  open,
  onClose,
  club,
  competitions,
  listInvitesByCompetition, // fn(competition_id) -> { items: [...] }
  assignOne, // fn(competition_id)
  removeOne, // fn(inviteId)
}) => {
  const [mapLoading, setMapLoading] = useState(true);
  const [inviteMap, setInviteMap] = useState({}); // compId -> invite or null
  const [toggling, setToggling] = useState({}); // compId -> bool loading

  const title = useMemo(
    () =>
      club
        ? `Gérer les compétitions — ${club.nom_club}`
        : "Gérer les compétitions",
    [club],
  );

  useEffect(() => {
    const buildMap = async () => {
      if (!open || !club || !Array.isArray(competitions)) return;
      setMapLoading(true);

      try {
        // 🔥 sécurisation des appels
        const results = await Promise.all(
          competitions.map((c) => {
            if (!c?.id) return Promise.resolve({ items: [] });
            return listInvitesByCompetition(c.id);
          }),
        );

        const map = {};

        competitions.forEach((c, idx) => {
          const items = Array.isArray(results[idx]?.items)
            ? results[idx].items
            : [];
          const found = items.find((it) => it.club_id === club.id) || null;
          map[c.id] = found;
        });

        setInviteMap(map);
      } catch (err) {
        console.error("Erreur buildMap:", err);
        toast.error("Impossible de charger les invitations existantes.");
      } finally {
        setMapLoading(false);
      }
    };

    buildMap();
  }, [open, club, competitions]);

  const toggleComp = async (compId, checked) => {
    setToggling((s) => ({ ...s, [compId]: true }));
    try {
      if (checked) {
        await assignOne(compId);
        // refetch rapide pour ce comp
        const res = await listInvitesByCompetition(compId);
        const found =
          (res.items || []).find((it) => it.club_id === club.id) || null;
        setInviteMap((s) => ({ ...s, [compId]: found }));
      } else {
        const invite = inviteMap[compId];
        if (invite?.id) {
          await removeOne(invite.id);
          setInviteMap((s) => ({ ...s, [compId]: null }));
        }
      }
    } catch {
      toast.error("Action impossible.");
    } finally {
      setToggling((s) => ({ ...s, [compId]: false }));
    }
  };

  return (
    <SlideOver open={open} onClose={onClose} title={title}>
      {mapLoading ? (
        <div className="p-6 text-center text-sm text-gray-500">Chargement…</div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white/80 dark:bg-slate-800/70 p-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Cochez les compétitions auxquelles{" "}
              <strong>{club.nom_club}</strong> doit être invité.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {competitions.map((c) => {
              const invited = !!inviteMap[c.id];
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-3 rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white/80 dark:bg-slate-800/70 p-3"
                >
                  <input
                    type="checkbox"
                    checked={invited}
                    onChange={(e) => toggleComp(c.id, e.target.checked)}
                    disabled={!!toggling[c.id]}
                  />
                  <div>
                    <div className="font-medium">{c.nom_competition}</div>
                    <div className="text-xs text-gray-500">
                      {new Date(c.date_competition).toLocaleDateString()}
                    </div>
                    {inviteMap[c.id]?.invite_url && (
                      <div className="mt-1 text-xs">
                        <a
                          href={inviteMap[c.id].invite_url}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          Lien d’invitation
                        </a>
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          <div className="pt-2 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              icon={FaTimes}
              onClick={onClose}
              className="rounded-lg"
            >
              Fermer
            </Button>
            <Button icon={FaSave} onClick={onClose} className="rounded-lg">
              Terminé
            </Button>
          </div>
        </div>
      )}
    </SlideOver>
  );
};

export default ManageClubCompetitionsModal;
